import React, { useState } from 'react';
import { Lead } from '../../../types';
import { useUsers } from '../../../contexts/users/UsersContext';
import { LeadInfoForm } from './lead-detail/LeadInfoForm';
import { LeadTasksTab } from './lead-detail/LeadTasksTab';
import { LeadOriginTab } from './lead-detail/LeadOriginTab';
import { LeadHistoryTab } from './lead-detail/LeadHistoryTab';
import { LeadProductsTab } from './lead-detail/LeadProductsTab';
import { XIcon } from '../../../components/icons';
import { useLeads } from '../../../contexts/leads/LeadsContext';
import { LeadHeader } from './lead-detail/LeadHeader';
import { useLeadDetails } from '../hooks/useLeadDetails';
import { useDealModals } from '../hooks/useDealModals';
import { WinDealModal } from './modals/WinDealModal';
import { LossReasonModal } from './modals/LossReasonModal';
import { cn } from '@/lib/utils';

interface OpportunityDetailsPanelProps {
    lead: Lead | null;
    onClose: () => void;
    onSave: (lead: Lead) => void;
    onDelete: (lead: Lead) => void;
}

const OpportunityDetailsPanel: React.FC<OpportunityDetailsPanelProps> = ({ lead, onClose, onSave, onDelete }) => {
    const { crmUsers } = useUsers();
    const { updateLeadStage } = useLeads();
    const [activeTab, setActiveTab] = useState('Tarefas');
    const {
        localLead,
        funnelStages,
        handleInputChange,
        handleUpdateLeadValue,
        handleSaveAndClose,
        handleObservationsDraft,
        setLocalLead
    } = useLeadDetails(lead, onSave);

    const leadsByStage = React.useMemo(() => {
        const currentLead = localLead || lead;
        return currentLead ? { [currentLead.stage]: [currentLead] } : {};
    }, [lead, localLead]);

    const {
        lossReasonModal,
        winDealModal,
        closeLossModal,
        closeWinModal,
        handleFinalStageDrop,
        handleConfirmWin: originalHandleConfirmWin,
        handleConfirmLoss: originalHandleConfirmLoss
    } = useDealModals(leadsByStage);

    const handleConfirmWin = async (value: number) => {
        if (localLead) {
            setLocalLead({ ...localLead, status: 'won', value: value });
        }
        await originalHandleConfirmWin(value);
    };

    const handleConfirmLoss = async (reasonId: string, observation: string) => {
        if (localLead) {
            setLocalLead({ ...localLead, status: 'lost' });
        }
        await originalHandleConfirmLoss(reasonId, observation);
    };

    const handleReopen = async () => {
        if (lead && funnelStages.length > 0) {
            await updateLeadStage(lead.id, funnelStages[0].id);
        }
    };

    if (!lead || !localLead) return null;

    const tabs = ['Tarefas', 'Produtos', 'Origem', 'Histórico'] as const;

    const renderTabContent = () => {
        switch (activeTab) {
            case 'Tarefas':
                return <LeadTasksTab lead={localLead} />;
            case 'Produtos':
                return <LeadProductsTab lead={localLead} onUpdateValue={handleUpdateLeadValue} />;
            case 'Origem':
                return <LeadOriginTab
                    lead={localLead}
                    onChange={(name, value) => handleInputChange({ target: { name, value } } as any)}
                />;
            case 'Histórico':
            default:
                return <LeadHistoryTab lead={localLead} />;
        }
    };

    return (
        <aside
            role="dialog"
            aria-modal="true"
            aria-label={`Detalhes da negociação ${localLead.name || 'sem nome'}`}
            className={cn(
                'fixed inset-y-0 right-0 z-40 flex h-full w-full flex-col overflow-hidden',
                'rounded-none border-l border-border bg-surface-0 shadow-lg',
                'transition-transform duration-[var(--slow,250ms)] ease-[cubic-bezier(.4,0,.2,1)]',
                'md:w-[500px] md:rounded-tl-xl lg:w-[550px]',
                lead ? 'translate-x-0' : 'translate-x-full',
            )}
        >
            <header className="flex shrink-0 items-center justify-start border-b border-border bg-surface-0 px-s4 py-s4">
                <button
                    type="button"
                    onClick={() => handleSaveAndClose(onClose)}
                    className="flex size-9 touch-manipulation items-center justify-center rounded-full bg-gray-900 text-primary-foreground shadow-sm transition-colors hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                    title="Fechar"
                    aria-label="Fechar painel"
                >
                    <XIcon className="size-3" aria-hidden />
                </button>
            </header>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                <LeadHeader
                    lead={lead}
                    localLead={localLead}
                    funnelStages={funnelStages}
                    onInputChange={handleInputChange}
                    onDelete={onDelete}
                    onMarkAsWon={() => handleFinalStageDrop(lead.id, 'won')}
                    onMarkAsLost={() => handleFinalStageDrop(lead.id, 'lost')}
                    onReopen={handleReopen}
                />

                <div className="min-h-0 flex-1 space-y-s6 overflow-y-auto px-s4 py-s4 md:px-s6">
                    <LeadInfoForm
                        lead={localLead}
                        crmUsers={crmUsers}
                        onInputChange={handleInputChange}
                        onObservationsDraftChange={handleObservationsDraft}
                    />

                    <section aria-label="Abas da negociação" className="pt-s4">
                        <nav
                            aria-label="Seções da negociação"
                            className="mb-s4 flex border-b border-border"
                        >
                            {tabs.map((tab) => (
                                <button
                                    key={tab}
                                    type="button"
                                    onClick={() => setActiveTab(tab)}
                                    aria-current={activeTab === tab ? 'page' : undefined}
                                    className={cn(
                                        'min-h-11 flex-1 touch-manipulation border-b-2 px-s2 py-s3 text-center text-sm font-medium transition-colors',
                                        activeTab === tab
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-secondary hover:text-default',
                                    )}
                                >
                                    {tab}
                                </button>
                            ))}
                        </nav>
                        <div role="tabpanel">{renderTabContent()}</div>
                    </section>
                </div>
            </div>

            <LossReasonModal
                isOpen={lossReasonModal.isOpen}
                leadName={lossReasonModal.leadName}
                onClose={closeLossModal}
                onConfirm={handleConfirmLoss}
            />

            <WinDealModal
                isOpen={winDealModal.isOpen}
                leadName={winDealModal.leadName}
                currentValue={winDealModal.currentValue}
                onClose={closeWinModal}
                onConfirm={handleConfirmWin}
            />
        </aside>
    );
};

export default OpportunityDetailsPanel;
